import { Evidence } from 'domain';
import CommonUtils from 'utils/CommonUtils';

const get = (evidenceId) => {
    const query = new Parse.Query(Evidence)
        .include(['sourceOwned', 'evidenceFormat', 'createdBy', 'subject']);

    return {
        type: 'GET_EVIDENCE',
        meta: { evidenceId },
        payload: query.get(evidenceId).then(async evidence => {
            const format = evidence.get('evidenceFormat');
            const formatType = format.get('filteredFor');
            
            let voucher = null;
            let literature = null;
            let file = null;
            const evidenceSubjectsData = await new Parse.Query('EvidenceSubject')
                .equalTo('evidence', evidence)
                .include('subject')
                .find();
            evidence.set('subjects', evidenceSubjectsData.map(s => s.get('subject')));
            
            if (formatType === 'Voucher') {
                const voucherData = await new Parse.Query('EvidenceVoucher')
                    .equalTo('evidence', evidence)
                    .include('repository')
                    .include('voucherSource')
                    .include('event')
                    .first();

                if (voucherData) {
                    voucher = {
                        repository: voucherData.get('repository'),
                        catalogNumber: voucherData.get('catalogNumber'),
                        additionalNumber: voucherData.get('additionalNumber'),
                        voucherSource: voucherData.get('voucherSource'),
                        event: voucherData.get('event')
                    };
                }
            } 
            else if (formatType === 'Literature') {
                const literatureData = await new Parse.Query('EvidenceLiterature')
                    .equalTo('evidence', evidence)
                    .include('event')
                    .first();
                
                if (literatureData) {
                    const authors = await new Parse.Query('LiteratureAuthor')
                        .equalTo('evidenceLiterature', literatureData)
                        .include('author')
                        .find();
                    literature = {
                        title: literatureData.get('title'),
                        doi: literatureData.get('doi'),
                        publisher: literatureData.get('publisher'),
                        year: literatureData.get('year'),
                        volume: literatureData.get('volume'),
                        issue: literatureData.get('issue'),
                        startPage: literatureData.get('startPage'),
                        endPage: literatureData.get('endPage'),
                        authoredBy: authors.map(a => a.get('author')),
                        event: literatureData.get('event')
                    };
                }
            }
            else if (formatType === 'File') {
                const fileData = await new Parse.Query('EvidenceFile')
                    .equalTo('evidence', evidence)
                    .include('event')
                    .first();
                
                if (fileData) {
                    file = {
                        name: fileData.get('name'),
                        url: fileData.get('url'),
                        event: fileData.get('event')
                    };
                }
            }

            evidence.set('voucher', voucher);
            evidence.set('literature', literature);
            evidence.set('file', file);

            return evidence;
        })
    };
};

const list = (filters = {}) => {
    const query = new Parse.Query(Evidence)
        .include([
            'sourceOwned', 
            'repository', 
            'observation',
            'evidenceFormat'
        ]);

    const evidenceFileQuery = new Parse.Query('EvidenceFile');
    const evidenceLiteratureQuery = new Parse.Query('EvidenceLiterature')
        .include(['evidenceLiteratureAuthors']);
    const evidenceVoucherQuery = new Parse.Query('EvidenceVoucher')
        .include(['repository', 'voucherSource']);

    if (filters.observation) {
        query.equalTo('observation', filters.observation);
    }

    if (filters.createdBy) {
        query.equalTo('createdBy', filters.createdBy);
    }

    return {
        type: 'LIST_EVIDENCE',
        meta: { filters },
        payload: query.find().then(async evidence => {
            const evidenceWithRelations = await Promise.all(evidence.map(async e => {
                const files = await evidenceFileQuery
                    .equalTo('evidence', e)
                    .find();
                const literature = await evidenceLiteratureQuery
                    .equalTo('evidence', e)
                    .first();
                const voucher = await evidenceVoucherQuery
                    .equalTo('evidence', e)
                    .first();
                
                e.set('files', files);
                e.set('literature', literature);
                e.set('voucher', voucher);
                return e;
            }));
            return evidenceWithRelations;
        })
    };
};

const create = async (evidence) => {
    const savedEvidence = await evidence.save();

    const subjects = evidence.get('subjects');
    if (subjects?.length) {
        const subjectPromises = subjects.map(subjectId => {
            const evidenceSubject = new Parse.Object('EvidenceSubject');
            evidenceSubject.set('evidence', savedEvidence);
            evidenceSubject.set('subject', CommonUtils.createPointer('Subject', subjectId));
            return evidenceSubject.save();
        });
        await Promise.all(subjectPromises);
    }

    const observations = evidence.get('observations');
    if (observations?.length) {
        const observationPromises = observations.map(observationId => {
            const observationEvidence = new Parse.Object('ObservationEvidence');
            observationEvidence.set('evidence', savedEvidence);
            observationEvidence.set('observation', CommonUtils.createPointer('Observation', observationId));
            return observationEvidence.save();
        });
        await Promise.all(observationPromises);
    }

    const evidenceFormatPointer = evidence.get('evidenceFormat');
    const formatQuery = new Parse.Query('EvidenceFormat');
    const evidenceFormat = await formatQuery.get(evidenceFormatPointer.objectId);
    const formatType = evidenceFormat.get('filteredFor');
    const voucherData = evidence.get('voucher');
    if (formatType === 'Voucher' && voucherData) {
        const evidenceVoucher = new Parse.Object('EvidenceVoucher');
        evidenceVoucher.set('evidence', savedEvidence);
        if (voucherData.repository) {
            evidenceVoucher.set('repository', CommonUtils.createPointer('Repository', voucherData.repository.id));
        }
        if (voucherData.voucherSource) {
            const voucherSourcePerson = voucherData.voucherSource.get('person');
            evidenceVoucher.set('voucherSource', CommonUtils.createPointer('Person', voucherSourcePerson.id));
        }
        evidenceVoucher.set('catalogNumber', voucherData.catalogNumber);
        evidenceVoucher.set('additionalNumber', voucherData.additionalNumber);
        if (voucherData.event) {
            evidenceVoucher.set('event', CommonUtils.createPointer('Event', voucherData.event.id));
        }
        await evidenceVoucher.save();
    }

    const litData = evidence.get('literature');
    if (formatType === 'Literature' && litData) {
        const evidenceLiterature = new Parse.Object('EvidenceLiterature');
        evidenceLiterature.set('evidence', savedEvidence);
        evidenceLiterature.set('title', litData.title || '');
        evidenceLiterature.set('doi', litData.doi || '');
        evidenceLiterature.set('publisher', litData.publisher || '');
        evidenceLiterature.set('year', litData.year || '');
        evidenceLiterature.set('volume', litData.volume || '');
        evidenceLiterature.set('issue', litData.issue || '');
        evidenceLiterature.set('startPage', litData.startPage || '');
        evidenceLiterature.set('endPage', litData.endPage || '');
        if (litData.event) {
            evidenceLiterature.set('event', CommonUtils.createPointer('Event', litData.event.id));
        }
        await evidenceLiterature.save();

        if (litData.authors?.length) {
            const authorPromises = litData.authors.map(author => {
                const literatureAuthor = new Parse.Object('LiteratureAuthor');
                const authorPersonData = author.get('person');
                literatureAuthor.set('evidenceLiterature', evidenceLiterature);
                literatureAuthor.set('author', CommonUtils.createPointer('Person', authorPersonData.id));
                return literatureAuthor.save();
            });
            const savedAuthors = await Promise.all(authorPromises);
            evidenceLiterature.set('authoredBy', savedAuthors.map(a => a.get('author')));
            await evidenceLiterature.save();
        }
    }

    const fileData = evidence.get('file');
    if (formatType === 'File' && fileData) {
        const evidenceFile = new Parse.Object('EvidenceFile');
        evidenceFile.set('evidence', savedEvidence);
        evidenceFile.set('name', fileData.name || '');
        evidenceFile.set('url', fileData.url || '');
        if (fileData.event) {
            evidenceFile.set('event', CommonUtils.createPointer('Event', fileData.event.id));
        }
        await evidenceFile.save();
    }

    return savedEvidence;
};

const update = async (evidence) => {
    const savedEvidence = await evidence.save();

    const subjects = evidence.get('subjects');
    
    const existingSubjectsQuery = new Parse.Query('EvidenceSubject');
    existingSubjectsQuery.equalTo('evidence', savedEvidence);
    const existingSubjects = await existingSubjectsQuery.find();
    if (existingSubjects.length > 0) {
        await Parse.Object.destroyAll(existingSubjects);
    }
    
    if (subjects?.length) {
        const subjectPromises = subjects.map(subjectId => {
            const evidenceSubject = new Parse.Object('EvidenceSubject');
            evidenceSubject.set('evidence', savedEvidence);
            evidenceSubject.set('subject', CommonUtils.createPointer('Subject', subjectId));
            return evidenceSubject.save();
        });
        await Promise.all(subjectPromises);
    }

    const observations = evidence.get('observations');
    
    const existingObservationsQuery = new Parse.Query('ObservationEvidence');
    existingObservationsQuery.equalTo('evidence', savedEvidence);
    const existingObservations = await existingObservationsQuery.find();
    if (existingObservations.length > 0) {
        await Parse.Object.destroyAll(existingObservations);
    }
    
    if (observations?.length) {
        const observationPromises = observations.map(observationId => {
            const observationEvidence = new Parse.Object('ObservationEvidence');
            observationEvidence.set('evidence', savedEvidence);
            observationEvidence.set('observation', CommonUtils.createPointer('Observation', observationId));
            return observationEvidence.save();
        });
        await Promise.all(observationPromises);
    }

    const evidenceFormatPointer = evidence.get('evidenceFormat');
    const formatQuery = new Parse.Query('EvidenceFormat');
    const evidenceFormat = await formatQuery.get(evidenceFormatPointer.objectId);
    const formatType = evidenceFormat.get('filteredFor');
    const voucherData = evidence.get('voucher');
    if (formatType === 'Voucher' && voucherData) {
        const voucherQuery = new Parse.Query('EvidenceVoucher');
        voucherQuery.equalTo('evidence', savedEvidence);
        let evidenceVoucher = await voucherQuery.first();
        
        if (!evidenceVoucher) {
            evidenceVoucher = new Parse.Object('EvidenceVoucher');
            evidenceVoucher.set('evidence', savedEvidence);
        }
        
        if (voucherData.repository) {
            evidenceVoucher.set('repository', CommonUtils.createPointer('Repository', voucherData.repository.id));
        }
        if (voucherData.voucherSource) {
            const voucherSourcePerson = voucherData.voucherSource.get('person');
            evidenceVoucher.set('voucherSource', CommonUtils.createPointer('Person', voucherSourcePerson.id));
        }
        evidenceVoucher.set('catalogNumber', voucherData.catalogNumber);
        evidenceVoucher.set('additionalNumber', voucherData.additionalNumber);
        if (voucherData.event) {
            evidenceVoucher.set('event', CommonUtils.createPointer('Event', voucherData.event.id));
        }
        await evidenceVoucher.save();
    }

    const litData = evidence.get('literature');
    if (formatType === 'Literature' && litData) {
        const literatureQuery = new Parse.Query('EvidenceLiterature');
        literatureQuery.equalTo('evidence', savedEvidence);
        let evidenceLiterature = await literatureQuery.first();
        
        if (!evidenceLiterature) {
            evidenceLiterature = new Parse.Object('EvidenceLiterature');
            evidenceLiterature.set('evidence', savedEvidence);
        }
        
        evidenceLiterature.set('title', litData.title || '');
        evidenceLiterature.set('doi', litData.doi || '');
        evidenceLiterature.set('publisher', litData.publisher || '');
        evidenceLiterature.set('year', litData.year || '');
        evidenceLiterature.set('volume', litData.volume || '');
        evidenceLiterature.set('issue', litData.issue || '');
        evidenceLiterature.set('startPage', litData.startPage || '');
        evidenceLiterature.set('endPage', litData.endPage || '');
        if (litData.event) {
            evidenceLiterature.set('event', CommonUtils.createPointer('Event', litData.event.id));
        }
        await evidenceLiterature.save();

        if (litData.authors?.length) {
            const existingAuthorsQuery = new Parse.Query('LiteratureAuthor');
            existingAuthorsQuery.equalTo('evidenceLiterature', evidenceLiterature);
            const existingAuthors = await existingAuthorsQuery.find();
            if (existingAuthors.length > 0) {
                await Parse.Object.destroyAll(existingAuthors);
            }
            
            const authorPromises = litData.authors.map(author => {
                const literatureAuthor = new Parse.Object('LiteratureAuthor');
                const authorPersonData = author.get('person');
                literatureAuthor.set('evidenceLiterature', evidenceLiterature);
                literatureAuthor.set('author', CommonUtils.createPointer('Person', authorPersonData.id));
                return literatureAuthor.save();
            });
            const savedAuthors = await Promise.all(authorPromises);
            evidenceLiterature.set('authoredBy', savedAuthors.map(a => a.get('author')));
            await evidenceLiterature.save();
        }
    }

    const fileData = evidence.get('file');
    if (formatType === 'File' && fileData) {
        const fileQuery = new Parse.Query('EvidenceFile');
        fileQuery.equalTo('evidence', savedEvidence);
        let evidenceFile = await fileQuery.first();
        
        if (!evidenceFile) {
            evidenceFile = new Parse.Object('EvidenceFile');
            evidenceFile.set('evidence', savedEvidence);
        }
        
        evidenceFile.set('name', fileData.name || '');
        evidenceFile.set('url', fileData.url || '');
        if (fileData.event) {
            evidenceFile.set('event', CommonUtils.createPointer('Event', fileData.event.id));
        }
        await evidenceFile.save();
    }

    return savedEvidence;
};

const save = (evidence) => {
    return {
        type: 'SAVE_EVIDENCE',
        payload: evidence.id ? update(evidence) : create(evidence)
    };
};

const attachToObservation = (evidenceId, observationId) => {
    return {
        type: 'ATTACH_EVIDENCE_TO_OBSERVATION',
        meta: { evidenceId, observationId },
        payload: (async () => {
            const checkQuery = new Parse.Query('ObservationEvidence');
            checkQuery.equalTo('evidence', CommonUtils.createPointer('Evidence', evidenceId));
            checkQuery.equalTo('observation', CommonUtils.createPointer('Observation', observationId));
            const existing = await checkQuery.first();
            
            if (existing) {
                return existing;
            }
            
            const observationEvidence = new Parse.Object('ObservationEvidence');
            observationEvidence.set('evidence', CommonUtils.createPointer('Evidence', evidenceId));
            observationEvidence.set('observation', CommonUtils.createPointer('Observation', observationId));
            return observationEvidence.save();
        })()
    };
}

const detachFromObservation = (evidenceId, observationId) => {
    return {
        type: 'DETACH_EVIDENCE_FROM_OBSERVATION',
        meta: { evidenceId, observationId },
        payload: (async () => {
            const query = new Parse.Query('ObservationEvidence');
            query.equalTo('evidence', CommonUtils.createPointer('Evidence', evidenceId));
            query.equalTo('observation', CommonUtils.createPointer('Observation', observationId));
            const records = await query.find();
            if (records.length > 0) {
                await Parse.Object.destroyAll(records);
            }
            return { evidenceId, observationId, deletedCount: records.length };
        })()
    };
}

const listForObservation = (observationId) => {
    return {
        type: 'LIST_EVIDENCE',
        meta: { observationId },
        payload: (async () => {
            const observationPointer = CommonUtils.createPointer('Observation', observationId);
            const observationEvidenceQuery = new Parse.Query('ObservationEvidence')
                .equalTo('observation', observationPointer)
                .include(['evidence', 'evidence.sourceOwned', 'evidence.repository', 'evidence.evidenceFormat']);
            
            const observationEvidences = await observationEvidenceQuery.find();
            const evidences = observationEvidences.map(oe => oe.get('evidence'));
            
            const evidenceFileQuery = new Parse.Query('EvidenceFile');
            const evidenceLiteratureQuery = new Parse.Query('EvidenceLiterature')
                .include('event');
            const evidenceVoucherQuery = new Parse.Query('EvidenceVoucher')
                .include(['repository', 'voucherSource']);
            
            const evidenceWithRelations = await Promise.all(evidences.map(async e => {
                const files = await evidenceFileQuery
                    .equalTo('evidence', e)
                    .find();
                const literature = await evidenceLiteratureQuery
                    .equalTo('evidence', e)
                    .first();
                const voucher = await evidenceVoucherQuery
                    .equalTo('evidence', e)
                    .first();
                
                // If literature exists, fetch its authors
                if (literature) {
                    const authors = await new Parse.Query('LiteratureAuthor')
                        .equalTo('evidenceLiterature', literature)
                        .include('author')
                        .find();
                    literature.set('authoredBy', authors.map(a => a.get('author')));
                }
                
                e.set('files', files);
                e.set('literature', literature);
                e.set('voucher', voucher);
                return e;
            }));
            
            return evidenceWithRelations;
        })()
    };
};

const deleteEvidence = (evidenceId) => {
    return {
        type: 'DELETE_EVIDENCE',
        payload: (async () => {
            const evidencePointer = CommonUtils.createPointer('Evidence', evidenceId);
            
            const subjectQuery = new Parse.Query('EvidenceSubject');
            subjectQuery.equalTo('evidence', evidencePointer);
            const evidenceSubjects = await subjectQuery.find();
            
            const voucherQuery = new Parse.Query('EvidenceVoucher');
            voucherQuery.equalTo('evidence', evidencePointer);
            const evidenceVouchers = await voucherQuery.find();
            
            const literatureQuery = new Parse.Query('EvidenceLiterature');
            literatureQuery.equalTo('evidence', evidencePointer);
            const evidenceLiteratures = await literatureQuery.find();
            
            for (const literature of evidenceLiteratures) {
                const authorQuery = new Parse.Query('LiteratureAuthor');
                authorQuery.equalTo('evidenceLiterature', literature);
                const literatureAuthors = await authorQuery.find();
                if (literatureAuthors.length > 0) {
                    await Parse.Object.destroyAll(literatureAuthors);
                }
            }
            
            const fileQuery = new Parse.Query('EvidenceFile');
            fileQuery.equalTo('evidence', evidencePointer);
            const evidenceFiles = await fileQuery.find();
            
            const observationEvidenceQuery = new Parse.Query('ObservationEvidence');
            observationEvidenceQuery.equalTo('evidence', evidencePointer);
            const observationEvidences = await observationEvidenceQuery.find();
            
            if (evidenceSubjects.length > 0) {
                await Parse.Object.destroyAll(evidenceSubjects);
            }
            if (evidenceVouchers.length > 0) {
                await Parse.Object.destroyAll(evidenceVouchers);
            }
            if (evidenceLiteratures.length > 0) {
                await Parse.Object.destroyAll(evidenceLiteratures);
            }
            if (evidenceFiles.length > 0) {
                await Parse.Object.destroyAll(evidenceFiles);
            }
            if (observationEvidences.length > 0) {
                await Parse.Object.destroyAll(observationEvidences);
            }
            
            const evidenceQuery = new Parse.Query('Evidence');
            const evidence = await evidenceQuery.get(evidenceId);
            await evidence.destroy();
            
            return {
                evidenceId,
                deletedSubjectsCount: evidenceSubjects.length,
                deletedVouchersCount: evidenceVouchers.length,
                deletedLiteraturesCount: evidenceLiteratures.length,
                deletedFilesCount: evidenceFiles.length,
                deletedObservationEvidencesCount: observationEvidences.length
            };
        })()
    };
};

export default {
    get,
    list,
    listForObservation,
    save,
    attachToObservation,
    detachFromObservation,
    deleteEvidence
};